import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { PreVisitQuestion } from '@/api/preVisit/preVisitApi.types';
import { useReplaceWorkspacePreVisitQuestionsMutation } from '@/hooks/usePreVisitQuestions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Icon } from '@/components/common/Icon';
import { FieldError } from '@/components/ui/fieldError';
import { PRE_VISIT_QUESTION_TYPE } from '@/constants/preVisitQuestionTypes';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { PreVisitFormPreview } from './PreVisitFormPreview';
import { PreVisitQuestionDialog } from './PreVisitQuestionDialog';
import { PreVisitQuestionRow } from './PreVisitQuestionRow';
import { RemovedPreVisitQuestionsList } from './RemovedPreVisitQuestionsList';
import {
  buildNewQuestionRow,
  buildRemovedQuestions,
  isQuestionRowValid,
  moveQuestionRow,
  toEditableQuestionRow,
  serializeQuestionRowsForComparison,
  toEditableQuestionRows,
  toQuestionDraft,
  type EditableQuestionRow,
} from './preVisitFormEditorState';

interface EditingState {
  row: EditableQuestionRow;
  isNew: boolean;
}

interface PreVisitFormEditorProps {
  initialQuestions: PreVisitQuestion[];
}

export function PreVisitFormEditor({ initialQuestions }: PreVisitFormEditorProps) {
  const { t } = useTranslation('consultations');
  const mutation = useReplaceWorkspacePreVisitQuestionsMutation();

  const [rows, setRows] = useState<EditableQuestionRow[]>(toEditableQuestionRows(initialQuestions));
  const [removedQuestions, setRemovedQuestions] = useState<PreVisitQuestion[]>(
    buildRemovedQuestions(initialQuestions)
  );
  const [baselineSnapshot, setBaselineSnapshot] = useState(
    serializeQuestionRowsForComparison(rows)
  );
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [rowPendingRemoval, setRowPendingRemoval] = useState<EditableQuestionRow | null>(null);
  const [isPreviewShownOnMobile, setIsPreviewShownOnMobile] = useState(false);
  const [hasInvalidRows, setHasInvalidRows] = useState(false);

  useEffect(
    function syncFromServer() {
      setRows(toEditableQuestionRows(initialQuestions));
      setRemovedQuestions(buildRemovedQuestions(initialQuestions));
      setBaselineSnapshot(
        serializeQuestionRowsForComparison(toEditableQuestionRows(initialQuestions))
      );
      setHasInvalidRows(false);
    },
    [initialQuestions]
  );

  const isDirty = serializeQuestionRowsForComparison(rows) !== baselineSnapshot;

  function openAddDialog() {
    setEditingState({ row: buildNewQuestionRow(PRE_VISIT_QUESTION_TYPE.shortText), isNew: true });
  }

  function openEditDialog(row: EditableQuestionRow) {
    setEditingState({ row, isNew: false });
  }

  function handleDialogSave(updatedRow: EditableQuestionRow) {
    setHasInvalidRows(false);
    setRows(function apply(current) {
      if (editingState === null || editingState.isNew) {
        return [...current, updatedRow];
      }
      return current.map(function replace(row) {
        return row.key === updatedRow.key ? updatedRow : row;
      });
    });
    setEditingState(null);
  }

  function moveRow(index: number, direction: -1 | 1) {
    setRows(function apply(current) {
      return moveQuestionRow(current, index, direction);
    });
  }

  function confirmRemoval() {
    if (rowPendingRemoval === null) {
      return;
    }
    setRows(function apply(current) {
      return current.filter(function keep(row) {
        return row.key !== rowPendingRemoval.key;
      });
    });
    setRowPendingRemoval(null);
  }

  function restoreQuestion(question: PreVisitQuestion) {
    setRows(function apply(current) {
      return [...current, toEditableQuestionRow(question)];
    });
    setRemovedQuestions(function apply(current) {
      return current.filter(function keep(removedQuestion) {
        return removedQuestion.code !== question.code;
      });
    });
  }

  async function save() {
    const hasInvalid = rows.some(function invalid(row) {
      return isQuestionRowValid(row) === false;
    });
    if (hasInvalid) {
      setHasInvalidRows(true);
      showErrorNotification(t('preVisitEditor.invalidQuestion'));
      return;
    }
    setHasInvalidRows(false);
    const drafts = rows.map(toQuestionDraft);
    try {
      const saved = await mutation.mutateAsync(drafts);
      const savedRows = toEditableQuestionRows(saved);
      setRows(savedRows);
      setRemovedQuestions(buildRemovedQuestions(saved));
      setBaselineSnapshot(serializeQuestionRowsForComparison(savedRows));
      showSuccessNotification(t('preVisitEditor.saved'));
    } catch (saveError) {
      const message = resolveApiErrorMessage(saveError);
      showErrorNotification(message.length > 0 ? message : t('preVisitEditor.saveError'));
    }
  }

  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="text-sm font-bold text-zooindigo">{t('preVisitEditor.title')}</h2>
          <p className="text-xs text-zoomuted mt-0.5">{t('preVisitEditor.hint')}</p>
        </div>
        {isDirty ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-zoowarningtext shrink-0">
            <Icon name="AlertCircle" />
            {t('preVisitEditor.unsavedChanges')}
          </span>
        ) : null}
      </div>

      <Button
        type="button"
        variant="outline"
        size="small"
        className="xl:hidden mt-3"
        onClick={function togglePreview() {
          setIsPreviewShownOnMobile(function invert(previous) {
            return previous === false;
          });
        }}
      >
        <Icon name="Eye" />
        {isPreviewShownOnMobile ? t('preVisitEditor.hidePreview') : t('preVisitEditor.showPreview')}
      </Button>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 mt-4">
        <div className="space-y-3 min-w-0">
          {rows.length === 0 ? (
            <p className="text-sm text-zoomuted">{t('preVisitEditor.empty')}</p>
          ) : (
            <div className="space-y-2">
              {rows.map(function renderRow(row, index) {
                return (
                  <PreVisitQuestionRow
                    key={row.key}
                    row={row}
                    isFirst={index === 0}
                    isLast={index === rows.length - 1}
                    onMoveUp={function moveUp() {
                      moveRow(index, -1);
                    }}
                    onMoveDown={function moveDown() {
                      moveRow(index, 1);
                    }}
                    onEdit={function edit() {
                      openEditDialog(row);
                    }}
                    onRequestRemoval={function requestRemoval() {
                      setRowPendingRemoval(row);
                    }}
                  />
                );
              })}
            </div>
          )}

          {hasInvalidRows ? (
            <FieldError messageKey="consultations:preVisitEditor.invalidQuestion" />
          ) : null}

          <Button type="button" variant="outline" size="small" onClick={openAddDialog}>
            <Icon name="Plus" /> {t('preVisitEditor.addQuestion')}
          </Button>

          <RemovedPreVisitQuestionsList questions={removedQuestions} onRestore={restoreQuestion} />
        </div>

        <div className={isPreviewShownOnMobile ? 'block min-w-0' : 'hidden xl:block min-w-0'}>
          <PreVisitFormPreview rows={rows} />
        </div>
      </div>

      <div className="flex justify-end mt-5">
        <Button type="button" isLoading={mutation.isPending} onClick={save}>
          {t('preVisitEditor.save')}
        </Button>
      </div>

      {editingState === null ? null : (
        <PreVisitQuestionDialog
          initialRow={editingState.row}
          isNew={editingState.isNew}
          onSave={handleDialogSave}
          onCancel={function cancel() {
            setEditingState(null);
          }}
        />
      )}

      {rowPendingRemoval === null ? null : (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('preVisitEditor.deactivateConfirmTitle')}
          description={t('preVisitEditor.deactivateConfirmBody')}
          confirmLabel={t('preVisitEditor.deactivate')}
          onConfirm={confirmRemoval}
          onCancel={function cancelRemoval() {
            setRowPendingRemoval(null);
          }}
        />
      )}
    </Card>
  );
}
